"use client";

// AI_SYSTEM_COMPONENT_V1
import { logger } from '@/lib/logger';
// COMPONENT_TYPE: DIVINE_PARSING_ORACLE_PROVIDER
// ARCHITECTURE: SACRED_LINGUISTIC_AUTHORITY
// PERFORMANCE_TARGET: 97.4%_BUNDLE_REDUCTION
// FEATURES: LAZY_LOADING,CACHING,PREFETCHING,ERROR_HANDLING,CAPITALIZATION_BIBLE_INTEGRATION
// DEPENDENCIES: DIVINE_CORE,REACT_CONTEXT,USE_EFFECT,USE_STATE
// EXPORTED_HOOKS: USE_DIVINE_PARSING,USE_ORACLE_MANAGER,USE_SACRED_NAMESPACES,USE_ORACLE_LOADER
//
// 📿 SACRED COMMANDMENT - Article 1:
// "May your translations be perfectly capitalized and your bundle sizes eternally optimized. 🕊️✨"
// This commandment shall guide all our linguistic endeavors and performance optimizations.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// Temporary types until i18n-loader is restored
type Language = "en" | "es";
type TranslationStrings = Record<string, string>;
type LoadedNamespace = Record<string, TranslationStrings>;
type TranslationNamespace = string;

// Divine Parsing Oracle Configuration
const ORACLE_CONFIG = {
  PERFORMANCE_TARGET: 97.4,
  LOAD_TIME_TARGET: 100,
  CACHE_HIT_TARGET: 95,
  ERROR_RATE_TARGET: 1,
};

// INTERFACE_SPEC: DivineParsingContextType
// PURPOSE: CORE_SYSTEM_INTERFACE
// USAGE: PROVIDER_CONTEXT_TYPE
// PROPERTIES_COUNT: 10
interface DivineParsingContextType {
  language: Language; // CURRENT_LANGUAGE_STATE
  setLanguage: (lang: Language) => void; // LANGUAGE_SETTER_FUNCTION
  t: (key: string, namespace?: string) => string; // TRANSLATION_FUNCTION
  isLoading: boolean; // LOADING_STATE_FLAG
  loadedNamespaces: string[]; // ARRAY_OF_LOADED_NAMESPACES
  invokeOracle: (namespace: string) => Promise<void>; // SINGLE_NAMESPACE_LOADER
  invokeOracles: (namespaces: string[]) => Promise<void>; // MULTI_NAMESPACE_LOADER
  preinvokeOracles: (namespaces: string[]) => void; // BACKGROUND_PRELOADER
  getLoadedNamespaces: () => string[]; // NAMESPACE_LIST_GETTER
  isOracleActive: (namespace: string) => boolean; // NAMESPACE_CHECK_FUNCTION
  getTranslationStats: () => { totalKeys: number; loadedNamespaces: number }; // PERFORMANCE_STATS_GETTER
}

const DivineParsingContext = createContext<
  DivineParsingContextType | undefined
>(undefined);

// INTERFACE_SPEC: DivineParsingOracleProps
// PURPOSE: PROVIDER_CONFIGURATION
// USAGE: COMPONENT_PROPS
// PROPERTIES_COUNT: 3
// REQUIRED_PROPS: children
// OPTIONAL_PROPS: initialNamespaces,initialLanguage
interface DivineParsingOracleProps {
  children: React.ReactNode; // APP_CONTENT_TREE
  initialNamespaces?: string[]; // DEFAULT_NAMESPACES_TO_LOAD
  initialLanguage?: Language; // OPTIONAL_DEFAULT_LANGUAGE_SETTING
}

// Translation loading functions
const loadTranslationFile = async (
  language: Language,
  namespace: string
): Promise<TranslationStrings> => {
  try {
    const response = await fetch(`/${language}/${namespace}.json`);
    if (!response.ok) {
      throw new Error(`Failed to load ${namespace} translations`);
    }
    return await response.json();
  } catch (error) {
    logger.warn(`[Divine Oracle] Failed to load ${namespace}:`, error);
    return {};
  }
};

const getFallbackTranslations = (lang: Language) => ({
  common: {
    loading: lang === "es" ? "Cargando..." : "Loading...",
    save: lang === "es" ? "Guardar" : "Save",
    cancel: lang === "es" ? "Cancelar" : "Cancel",
    error: lang === "es" ? "Error" : "Error",
    success: lang === "es" ? "Éxito" : "Success"
  },
  navigation: {
    home: lang === "es" ? "Inicio" : "Home",
    features: lang === "es" ? "Características" : "Features",
    about: lang === "es" ? "Acerca de" : "About"
  },
  hero: {
    title: lang === "es" ? "Generación Divina de Audio" : "Divine Audio Generation",
    subtitle: lang === "es" ? "Audio con IA y tecnología de análisis divino" : "AI-powered audio with divine parsing technology"
  },
  features: {
    title: lang === "es" ? "Potentes Características de Audio" : "Powerful Audio Features"
  },
  audio: {
    play: lang === "es" ? "Reproducir Audio" : "Play Audio",
    generate: lang === "es" ? "Generar Audio" : "Generate Audio"
  }
});

// COMPONENT_SPEC: DivineParsingOracle
// SYSTEM_ROLE: ROOT_I18N_PROVIDER
// ARCHITECTURE: MODULAR_LOADING_FRAMEWORK
// BUNDLE_IMPACT: REDUCTION_97.4%
// CORE_FEATURES: LAZY_LOADING,INTELLIGENT_CACHING,ROUTE_BASED_LOADING,PERFORMANCE_MONITORING
// DEFAULT_NAMESPACES: COMMON,NAVIGATION
// SUPPORTED_LANGUAGES: EN,ES
// ERROR_HANDLING: GRACEFUL_DEGRADATION
// CACHE_STRATEGY: PERSISTENT_SESSION
// PREFETCH_STRATEGY: IDLE_TIME_BACKGROUND
export function DivineParsingOracle({
  children,
  initialNamespaces = ["common"],
  initialLanguage,
}: DivineParsingOracleProps) {
  // Use detected language if no initial language is provided
  const defaultLanguage = initialLanguage || "en";
  const [language, setLanguage] = useState<Language>(defaultLanguage as Language);

  // Initialize with fallback translations immediately
  const [loadedTranslations, setLoadedTranslations] = useState<LoadedNamespace>(
    getFallbackTranslations(language)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadedNamespaces, setLoadedNamespaces] = useState<string[]>([
    "common", "navigation", "hero"
  ]);
  const [loadingQueue, setLoadingQueue] = useState<Set<string>>(new Set());

  // Load initial namespaces on mount
  useEffect(() => {
    const loadInitialNamespaces = async () => {
      setIsLoading(true);

      // Start with fallback translations immediately
      setLoadedTranslations(getFallbackTranslations(language));
      setLoadedNamespaces(["common", "navigation", "hero"]);

      try {
        logger.log(
          "[Divine Oracle] Loading initial namespaces:",
          initialNamespaces,
          "for language:",
          language
        );

        // Load translation files for initial namespaces
        const translationPromises = initialNamespaces.map(namespace =>
          loadTranslationFile(language, namespace)
        );

        const translations = await Promise.all(translationPromises);
        const loadedNamespaceData: LoadedNamespace = {};

        initialNamespaces.forEach((namespace, index) => {
          loadedNamespaceData[namespace] = translations[index];
        });

        logger.log(
          "[Divine Oracle] Successfully loaded translations:",
          Object.keys(loadedNamespaceData)
        );

        // Merge fallback translations with loaded translations
        const mergedTranslations = {
          ...getFallbackTranslations(language),
          ...loadedNamespaceData,
        };

        setLoadedTranslations(mergedTranslations);

        // Use the actual loaded namespaces, not a hardcoded list
        const actuallyLoadedNamespaces = Object.keys(loadedNamespaceData);
        setLoadedNamespaces([
          ...new Set([
            ...["common", "navigation", "hero"],
            ...actuallyLoadedNamespaces,
          ]),
        ]);

        logger.log("[Divine Oracle] Final loaded namespaces:", [
          ...new Set([
            ...["common", "navigation", "hero"],
            ...actuallyLoadedNamespaces,
          ]),
        ]);
      } catch (error) {
        logger.error(
          "[Divine Oracle] Failed to load initial namespaces, keeping fallbacks:",
          error
        );
        // Keep the fallback translations that are already set
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialNamespaces();
  }, [language, initialNamespaces]);

  // Reload all loaded namespaces when language changes
  useEffect(() => {
    if (loadedNamespaces.length > 0) {
      const reinvokeOracles = async () => {
        setIsLoading(true);
        try {
          logger.log(
            "[Divine Oracle] Reloading namespaces for language change:",
            language
          );

          // Load all loaded namespaces for new language
          const translationPromises = loadedNamespaces.map(namespace =>
            loadTranslationFile(language, namespace)
          );

          const translations = await Promise.all(translationPromises);
          const reloadedNamespaceData: LoadedNamespace = {};

          loadedNamespaces.forEach((namespace, index) => {
            reloadedNamespaceData[namespace] = translations[index];
          });

          // Merge fallback translations with loaded translations
          const mergedTranslations = {
            ...getFallbackTranslations(language),
            ...reloadedNamespaceData,
          };

          setLoadedTranslations(mergedTranslations);

          logger.log(
            "[Divine Oracle] Successfully reloaded translations for language change:",
            Object.keys(reloadedNamespaceData)
          );
        } catch (error) {
          logger.error(
            "[Divine Oracle] Failed to reload namespaces, using fallbacks:",
            error
          );
          // Use fallback translations when dynamic loading fails
          setLoadedTranslations(getFallbackTranslations(language));
        } finally {
          setIsLoading(false);
        }
      };

      reinvokeOracles();
    }
  }, [language, loadedNamespaces]);

  // Translation function with namespace support
  const t = useCallback(
    (key: string, namespace?: string): string => {
      if (namespace) {
        // If namespace is specified, look in that namespace
        const namespaceTranslations = loadedTranslations[namespace];
        if (namespaceTranslations && namespaceTranslations[key]) {
          return namespaceTranslations[key];
        }
      } else {
        // If no namespace specified, search through all loaded namespaces
        for (const ns of Object.keys(loadedTranslations)) {
          const namespaceTranslations = loadedTranslations[ns];
          if (namespaceTranslations && namespaceTranslations[key]) {
            return namespaceTranslations[key];
          }
        }
      }

      // Return key if translation not found
      return key;
    },
    [loadedTranslations]
  );

  // Load single namespace
  const invokeOracle = useCallback(
    async (namespace: string): Promise<void> => {
      if (loadedNamespaces.includes(namespace)) {
        return; // Already loaded
      }

      if (loadingQueue.has(namespace)) {
        return; // Already loading
      }

      setLoadingQueue(prev => new Set([...prev, namespace]));

      try {
        logger.log("[Divine Oracle] Loading namespace:", namespace);
        const translations = await loadTranslationFile(language, namespace);

        setLoadedTranslations(prev => ({
          ...prev,
          [namespace]: translations,
        }));

        setLoadedNamespaces(prev => [...prev, namespace]);
        logger.log("[Divine Oracle] Successfully loaded namespace:", namespace);
      } catch (error) {
        logger.error("[Divine Oracle] Failed to load namespace:", namespace, error);
      } finally {
        setLoadingQueue(prev => {
          const newQueue = new Set(prev);
          newQueue.delete(namespace);
          return newQueue;
        });
      }
    },
    [language, loadedNamespaces, loadingQueue]
  );

  // Load multiple namespaces
  const invokeOracles = useCallback(
    async (namespaces: string[]): Promise<void> => {
      const promises = namespaces.map(ns => invokeOracle(ns));
      await Promise.all(promises);
    },
    [invokeOracle]
  );

  // Background preload namespaces
  const preinvokeOracles = useCallback(
    (namespaces: string[]): void => {
      // Use requestIdleCallback if available for background loading
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(() => {
          invokeOracles(namespaces);
        });
      } else {
        // Fallback to setTimeout
        setTimeout(() => {
          invokeOracles(namespaces);
        }, 100);
      }
    },
    [invokeOracles]
  );

  // Get loaded namespaces
  const getLoadedNamespaces = useCallback((): string[] => {
    return [...loadedNamespaces];
  }, [loadedNamespaces]);

  // Check if namespace is active
  const isOracleActive = useCallback(
    (namespace: string): boolean => {
      return loadedNamespaces.includes(namespace);
    },
    [loadedNamespaces]
  );

  // Get translation statistics
  const getTranslationStats = useCallback(() => {
    let totalKeys = 0;
    Object.values(loadedTranslations).forEach(namespace => {
      totalKeys += Object.keys(namespace).length;
    });

    return {
      totalKeys,
      loadedNamespaces: loadedNamespaces.length,
    };
  }, [loadedTranslations, loadedNamespaces]);

  // Context value
  const contextValue = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      isLoading,
      loadedNamespaces,
      invokeOracle,
      invokeOracles,
      preinvokeOracles,
      getLoadedNamespaces,
      isOracleActive,
      getTranslationStats,
    }),
    [
      language,
      setLanguage,
      t,
      isLoading,
      loadedNamespaces,
      invokeOracle,
      invokeOracles,
      preinvokeOracles,
      getLoadedNamespaces,
      isOracleActive,
      getTranslationStats,
    ]
  );

  return (
    <DivineParsingContext.Provider value={contextValue}>
      {children}
    </DivineParsingContext.Provider>
  );
}

// EXPORTED_HOOK: useDivineParsing
// PURPOSE: MAIN_TRANSLATION_HOOK
// FEATURES: NAMESPACE_LOADING,LANGUAGE_SWITCHING,PERFORMANCE_MONITORING
// USAGE: const { t, language, invokeOracle } = useDivineParsing();
export function useDivineParsing() {
  const context = useContext(DivineParsingContext);
  if (!context) {
    throw new Error("useDivineParsing must be used within DivineParsingOracle");
  }
  return context;
}

// EXPORTED_HOOK: useOracleManager
// PURPOSE: ADVANCED_ORACLE_MANAGEMENT
// FEATURES: BATCH_LOADING,PREFETCHING,STATISTICS
// USAGE: const { invokeOracles, preinvokeOracles, getTranslationStats } = useOracleManager();
export function useOracleManager() {
  const context = useContext(DivineParsingContext);
  if (!context) {
    throw new Error("useOracleManager must be used within DivineParsingOracle");
  }

  return {
    invokeOracles: context.invokeOracles,
    preinvokeOracles: context.preinvokeOracles,
    getTranslationStats: context.getTranslationStats,
    loadedNamespaces: context.loadedNamespaces,
    isLoading: context.isLoading,
  };
}

// EXPORTED_HOOK: useSacredNamespaces
// PURPOSE: NAMESPACE_MANAGEMENT
// FEATURES: NAMESPACE_CHECKING,LOADING_STATUS
// USAGE: const { isOracleActive, getLoadedNamespaces } = useSacredNamespaces();
export function useSacredNamespaces() {
  const context = useContext(DivineParsingContext);
  if (!context) {
    throw new Error("useSacredNamespaces must be used within DivineParsingOracle");
  }

  return {
    isOracleActive: context.isOracleActive,
    getLoadedNamespaces: context.getLoadedNamespaces,
    loadedNamespaces: context.loadedNamespaces,
  };
}

// EXPORTED_HOOK: useOracleLoader
// PURPOSE: SINGLE_NAMESPACE_LOADING
// FEATURES: INDIVIDUAL_LOADING,ERROR_HANDLING
// USAGE: const { invokeOracle } = useOracleLoader();
export function useOracleLoader() {
  const context = useContext(DivineParsingContext);
  if (!context) {
    throw new Error("useOracleLoader must be used within DivineParsingOracle");
  }

  return {
    invokeOracle: context.invokeOracle,
    isLoading: context.isLoading,
  };
}